import React, { useState } from "react";
import {
  Modal,
  Stack,
  TextInput,
  Button,
  Text,
  Checkbox,
  Alert,
} from "@mantine/core";
import { generateNewKeyPair, type KeyPair } from "../lib/keys";
import { uploadKey, requestVerification } from "../lib/key-server";

type CreateKeyModalProps = {
  opened: boolean;
  onClose: () => void;
  onCreated: (key: KeyPair) => void;
};

export function CreateKeyModal({
  opened,
  onClose,
  onCreated,
}: CreateKeyModalProps) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState<KeyPair | null>(null);
  const [backupConfirmed, setBackupConfirmed] = useState(false);
  const [publishToRegistry, setPublishToRegistry] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const newKey = await generateNewKeyPair(name, email, passphrase);
      setGeneratedKey(newKey);

      // Auto-download keys
      downloadKey(newKey, "public");
      downloadKey(newKey, "private");
    } catch (e) {
      console.error(e);
      alert("Failed to generate key");
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirm = async () => {
    if (generatedKey && backupConfirmed) {
      if (publishToRegistry) {
        setPublishing(true);
        try {
          const { token } = await uploadKey(generatedKey.publicKeyArmored);
          // Automatically request verification email
          await requestVerification(token, generatedKey.email);
          generatedKey.registryStatus = "pending";
          alert(
            `Verification email sent to ${generatedKey.email}. Please click the link to verify your identity on keys.openpgp.org.`,
          );
        } catch (e) {
          console.error("Failed to publish key:", e);
          alert(
            "Failed to publish key to the registry. You can try again later in Key Management.",
          );
        } finally {
          setPublishing(false);
        }
      }
      onCreated(generatedKey);
      reset();
    }
  };

  const reset = () => {
    setName("");
    setEmail("");
    setPassphrase("");
    setGeneratedKey(null);
    setBackupConfirmed(false);
    setPublishToRegistry(false);
    onClose();
  };

  const downloadKey = (key: KeyPair, type: "public" | "private") => {
    const content =
      type === "public" ? key.publicKeyArmored : key.privateKeyArmored;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${key.name.replace(/\s+/g, "_")}_${type}_key.asc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      opened={opened}
      onClose={reset}
      title={
        generatedKey ? "Key Generated Successfully" : "Create New Digital Key"
      }
      centered
      closeOnClickOutside={!generatedKey}
      closeOnEscape={!generatedKey}
      withCloseButton={!generatedKey}
    >
      <Stack>
        {!generatedKey ? (
          <>
            <Alert color="orange" title="Security Warning">
              <Text size="sm" mb="sm">
                Since we automatically save a backup of your keys, you can
                always restore your key on another device that you control. Do
                not share these keys with others and do not use on a shared
                device.
              </Text>
            </Alert>
            <Text size="sm" c="dimmed">
              This key will be stored locally in your browser and used to
              cryptographically sign documents. We'll also download the key
              files for you to keep as a backup.
            </Text>
            <TextInput
              label="Full Name"
              placeholder="John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <TextInput
              label="Email Address"
              placeholder="john@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <TextInput
              label="Passphrase (Required)"
              description="Your private key MUST be encrypted. If you lose this, you lose your key."
              placeholder="Create a strong password"
              type="password"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              required
            />
            <Button
              fullWidth
              onClick={handleGenerate}
              loading={generatedKey ? false : generating} // Don't show loading if done
              disabled={!name || !email || !passphrase}
            >
              Generate & Download Keys
            </Button>
          </>
        ) : (
          <>
            <Alert color="green" title="KeyPair Generated">
              Your new digital identity has been created and the keys should
              have started downloading automatically.
            </Alert>

            <Text size="sm" fw={700}>
              Important:
            </Text>
            <Text size="sm">
              Please verify that you have saved the <b>public</b> and{" "}
              <b>private</b> key files to a secure location. Possession of these
              files is the ONLY way to restore your identity on another device.
            </Text>

            <Checkbox
              label="I confirm that I have saved a backup of my keys"
              checked={backupConfirmed}
              onChange={(e) => setBackupConfirmed(e.currentTarget.checked)}
              pt="md"
            />
            <Checkbox
              label="Publish my public key to keys.openpgp.org for easier verification by others"
              description="Recommended. A verification email will be sent to confirm."
              checked={publishToRegistry}
              onChange={(e) => setPublishToRegistry(e.currentTarget.checked)}
              mb="md"
            />

            <Button
              fullWidth
              color="green"
              disabled={!backupConfirmed || publishing}
              loading={publishing}
              onClick={handleConfirm}
            >
              Complete Setup
            </Button>

            <Button
              variant="subtle"
              size="xs"
              onClick={() => {
                downloadKey(generatedKey, "public");
                downloadKey(generatedKey, "private");
              }}
            >
              Download Again
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}
